import type * as z from "zod";
import type { interactWithPage as interactWithPageTool } from "../../api/ai/tools/interactWithPage";
import { useEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import type { DriveCommand } from "../lib/webpageDebugBridge";

export type WebpageDebugArgs = {
  drive: (
    step: string,
    command: DriveCommand
  ) => Promise<{
    dom?: string;
    console?: { level: string; text: string }[];
    actionErrors?: { index: number; error: string }[];
    image?: { mediaType: string; data: string };
    error?: string;
  }>;
};

type Input = Partial<
  z.infer<ReturnType<typeof interactWithPageTool>["inputSchema"]>
>;

const DOM_LIMIT = 80_000;

// Front-biased: for rendered markup the meaningful content is at the top of the
// body, so keep the start and drop the tail — unlike logs, where the end matters.
function truncate(text: string | undefined): {
  text: string | null;
  truncated: boolean;
} {
  if (!text) return { text: null, truncated: false };
  if (text.length <= DOM_LIMIT) return { text, truncated: false };
  const omitted = text.length - DOM_LIMIT;
  return {
    text: `${text.slice(0, DOM_LIMIT)}\n\n[… ${omitted.toLocaleString()} chars truncated …]`,
    truncated: true,
  };
}

function header(label: string) {
  return function Header({ result, interrupted }: ToolUseProps) {
    const output = result?.output as
      | { ok?: boolean; error?: string }
      | undefined;
    if (!output) {
      return <ToolStatus icon="compass" text={label} pending={!interrupted} />;
    }
    if (output.ok === false) {
      return (
        <ToolStatus
          icon="circle-off"
          iconColor="var(--red-600)"
          text={output.error ?? "Failed"}
        />
      );
    }
    return (
      <ToolStatus
        icon="check-filled"
        iconColor="var(--green-600)"
        text={`${label} — done`}
      />
    );
  };
}

type DriveResult = { output: unknown; isError: boolean };
type OnResult = (output: unknown, isError: boolean) => Promise<boolean>;
type Save = Promise<DriveResult | null>;

// Keyed by tool call so a remounted runner neither repeats the browser action
// nor saves its result twice; each entry resolves to the result if saving failed.
const saves = new Map<string, Save>();

function startSave(
  toolCallId: string,
  result: Promise<DriveResult>,
  onResult: OnResult
): Save {
  const save = result.then(async (r) =>
    (await onResult(r.output, r.isError)) ? null : r
  );
  saves.set(toolCallId, save);
  void save.then((failed) => {
    if (!failed && saves.get(toolCallId) === save) saves.delete(toolCallId);
  });
  return save;
}

async function runDrive(
  args: WebpageDebugArgs,
  step: string | undefined,
  command: DriveCommand
): Promise<DriveResult> {
  if (!step) {
    return { output: { ok: false, error: "No step specified" }, isError: true };
  }
  try {
    const result = await args.drive(step, command);
    if (result.error) {
      return { output: { ok: false, error: result.error }, isError: true };
    }
    const dom = truncate(result.dom);
    return {
      output: {
        ok: true,
        dom: dom.text ?? undefined,
        domTruncated: dom.truncated,
        console: result.console,
        actionErrors: result.actionErrors,
        // Field name the model-message serializer reads to send these as
        // real image content rather than base64 text.
        imageParts: result.image ? [result.image] : undefined,
      },
      isError: false,
    };
  } catch (e) {
    return {
      output: { ok: false, error: e instanceof Error ? e.message : String(e) },
      isError: true,
    };
  }
}

// Fires the drive once per tool call and saves its result even if this runner
// unmounts first; whichever runner is mounted offers the retry.
function DriveRunner({
  toolCallId,
  args,
  step,
  command,
  onResult,
}: {
  toolCallId: string;
  args: WebpageDebugArgs;
  step: string | undefined;
  command: DriveCommand;
  onResult: OnResult;
}) {
  const save = useRef<Save | null>(null);
  const [failed, setFailed] = useState<DriveResult | null>(null);
  useEffect(() => {
    save.current ??=
      saves.get(toolCallId) ??
      startSave(toolCallId, runDrive(args, step, command), onResult);
    let mounted = true;
    void save.current.then((result) => {
      if (mounted) setFailed(result);
    });
    return () => {
      mounted = false;
    };
  }, [toolCallId, args, step, command, onResult]);
  const retry = (result: DriveResult) => {
    setFailed(null);
    save.current = startSave(toolCallId, Promise.resolve(result), onResult);
    void save.current.then(setFailed);
  };
  return failed ? (
    <Button onClick={() => retry(failed)}>Retry saving result</Button>
  ) : null;
}

const inspectRenderedPageActivity = {
  name: "inspectRenderedPage" as const,
  Header: header("Inspecting rendered page"),
  alwaysLoadOutput: true,
};

const getPageConsoleActivity = {
  name: "getPageConsole" as const,
  Header: header("Reading page console"),
  alwaysLoadOutput: true,
};

const interactWithPageActivity = {
  name: "interactWithPage" as const,
  Header: header("Interacting with page"),
  alwaysLoadOutput: true,
};

const DRIVE_COMMANDS = {
  inspectRenderedPage: (input: Input): DriveCommand => ({
    kind: "inspect",
    selector: input.selector,
  }),
  getPageConsole: (): DriveCommand => ({ kind: "console" }),
  interactWithPage: (input: Input): DriveCommand => ({
    kind: "interact",
    actions: input.actions ?? [],
    selector: input.selector,
  }),
  screenshotPage: (): DriveCommand => ({ kind: "screenshot" }),
};

export type WebpageDebugToolName = keyof typeof DRIVE_COMMANDS;

export const isWebpageDebugTool = (
  name: string
): name is WebpageDebugToolName => Object.hasOwn(DRIVE_COMMANDS, name);

export function WebpageDebugPrompt({
  toolCallId,
  toolName,
  input,
  args,
  onResult,
}: {
  toolCallId: string;
  toolName: WebpageDebugToolName;
  input: unknown;
  args: WebpageDebugArgs;
  onResult: (output: unknown, isError: boolean) => Promise<boolean>;
}) {
  const parsed = (input as Input | null) ?? {};
  return (
    <DriveRunner
      toolCallId={toolCallId}
      args={args}
      step={parsed.step}
      command={DRIVE_COMMANDS[toolName](parsed)}
      onResult={onResult}
    />
  );
}

const withDrive =
  (activity: ChatTool & { name: WebpageDebugToolName }) =>
  (args: WebpageDebugArgs): ChatTool => ({
    ...activity,
    PendingPrompt: ({ toolCallId, input, onResult }) => (
      <WebpageDebugPrompt
        toolCallId={toolCallId}
        toolName={activity.name}
        input={input}
        args={args}
        onResult={onResult}
      />
    ),
  });

export const inspectRenderedPage = withDrive(inspectRenderedPageActivity);
export const getPageConsole = withDrive(getPageConsoleActivity);
export const interactWithPage = withDrive(interactWithPageActivity);

function ScreenshotBody({ result }: ToolUseProps) {
  const output = result?.output as
    | { imageParts?: { mediaType: string; data: string }[] }
    | undefined;
  const image = output?.imageParts?.[0];
  if (!image) return null;
  return (
    <img
      src={`data:${image.mediaType};base64,${image.data}`}
      alt="Page screenshot"
      style={{
        maxWidth: "100%",
        borderRadius: 6,
        border: "1px solid var(--alpha8)",
      }}
    />
  );
}

const screenshotPageActivity = {
  name: "screenshotPage" as const,
  Header: header("Taking screenshot"),
  Body: ScreenshotBody,
  alwaysLoadOutput: true,
};

export const webpageDebugActivity: readonly ChatTool[] = [
  inspectRenderedPageActivity,
  getPageConsoleActivity,
  interactWithPageActivity,
  screenshotPageActivity,
];

export const screenshotPage = withDrive(screenshotPageActivity);
